import { GridValidRowModel } from '@mui/x-data-grid-premium'
import { GridCellParams } from '@mui/x-data-grid-pro'
import cn from 'classnames'
import React from 'react'
import styles from 'src/components/DataGrid/cells/DataGridCell/DataGridCell.style'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'

export interface DataGridCellProps<Row extends GridValidRowModel> {
  text?: React.ReactNode
  subtext?: React.ReactNode
  icon?: React.ReactElement
  color?: 'highlight' | 'severe'
  bold?: boolean
  disabled?: boolean
  className?: string
  hideText?: boolean
  params: GridCellParams<Row>
}

const DataGridCell = <Row extends GridValidRowModel>(
  props: DataGridCellProps<Row> & Record<`data-${string}`, string | undefined>,
): React.ReactElement => {
  const {
    text,
    subtext,
    icon,
    color,
    bold,
    disabled,
    className,
    hideText,
    params,
    ...rest
  } = props
  const alignRight = params.colDef.align === 'right'

  // if no text provided, try to fallback to value if it's string compliant
  const getValue = (): React.ReactNode | undefined => {
    if (text !== undefined) {
      return text
    }
    if (
      params.value !== undefined &&
      (typeof params.value === 'string' || typeof params.value === 'number')
    ) {
      return params.value
    }
    return undefined
  }

  const textIsEmpty =
    getValue() === undefined || (typeof text === 'string' && text.trim() === '')
  return (
    <DataGridCellWrapper
      className={className}
      css={styles}
      disabled={disabled}
      params={params}
      {...rest}
    >
      <div className={cn('wrapper', color)}>
        {icon}
        <div className={cn('text-container', { bold, alignRight })}>
          <span className={cn('text', color)}>
            {textIsEmpty && !hideText ? '--' : getValue()}
          </span>
          {subtext && <span className={cn('subtext', color)}>{subtext}</span>}
        </div>
      </div>
    </DataGridCellWrapper>
  )
}

export default React.memo(DataGridCell)

export const renderCell = <Row extends GridValidRowModel>(
  params: GridCellParams<Row>,
) => <DataGridCell params={params} />
